import { useForm as createUseForm } from "@ant-design-vue/use";
import { Button, Col, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../../utils/button";
import { modalStyleForm, modalStyleIFrame } from "../../../../../utils/constant";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { has } from "../../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../../utils/page";
import {
    email,
    equalsLength, isAlphabetOrNumber,
    noHyphenValidator,
    required,
    requiredWhen,
    validateError
} from "../../../../../utils/validate";
import { createConditionAction, createConditionForm } from "./user.condition";
import { createColumns, createField } from "./user.section";

const CIFrame = defineAsyncComponent(() => import("../../../../../components/iframe/CIFrame"));
const ChangePassword = defineAsyncComponent(() => import("./ChangePassword"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/security/user";
export const routerPrefix = "users";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...where, { name: "isTemp", type: "Boolean", operator: "=", value: false }], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [{ name: "userRoles", select: ["*", "refRole.*"], page: 0, size: 1000000 }]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [{ name: "userRoles", select: ["refRole.name"], page: 0, size: 1000000 }]
        };
    }
};

export const createForm = () => {
    return {
        username: null,
        password: null,
        passwordConfirm: null,
        refImage: null,
        refTitle: null,
        firstNameTh: null,
        middleNameTh: null,
        lastNameTh: null,
        fullNameTh: null,
        firstNameEn: null,
        middleNameEn: null,
        lastNameEn: null,
        fullNameEn: null,
        outDate: null,
        expireDate: null,
        birthDate: null,
        email: null,
        idCard: null,
        mobile: null,
        fax: null,
        refDepartment: null,
        refJobPosition: null,
        refOfficerType: null,
        registerDate: null,
        refDocumentTypePerson: null,
        refNationality: null,
        refRace: null,
        refReligion: null,
        refGender: null,
        refDepartmentRegister: null,
        companyNameTh: null,
        companyNameEn: null,
        refCompanyType: null,
        refStatus: null,
        refParent: null,
        refPersonType: null,
        isCeo: null,
        isEmployee: null,
        refOfficerLv: null,
        isVerify: null,
        userRoles: [],
        forgotPasswords: [],
        addresses: [],
        userOperatorTypes: [],
        userObjectives: [],
        userBanks: [],
        attachments: [],
        attachmentCas: [],
        statuses: [],
        userRequestOwners: [],
        userCommittees: [],
        userAuthorizes: [],
        userCas: []
    };
};

export const createRules = ({ data }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    return {
        username: [required],
        firstNameTh: [requiredWhen({ when: () => modelRef?.refPersonType?.key !== "1" })],
        firstNameEn: [requiredWhen({ when: () => modelRef?.refPersonType?.key !== "1" })],
        companyNameTh: [requiredWhen({ when: () => modelRef?.refPersonType?.key === "1" })],
        companyNameEn: [requiredWhen({ when: () => modelRef?.refPersonType?.key === "1" })],
        email: [required, email],
        idCard: [required, noHyphenValidator, isAlphabetOrNumber, equalsLength(13)],
        mobile: [requiredWhen({ when: () => modelRef?.refPersonType?.key !== "1" })]
    };
};

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.username : i18n.global.t("button.create");
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise.then(page => initUseForm({ data, page, routerPrefix })).finally(() => setIsLoading(false));
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.username).join(", ") }),
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageArchive({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.username).join(", "), okType: "danger" }),
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageDelete({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.username).join(", "), okType: "danger" }),
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs }), classStyle: "m-1" }));
    }
    if (id && isActive && has({ menuContext, key: "canChangePassword" })) {
        col2.push(
            <Button class="btn-warning m-1" size="small" onClick={() => openModalChangePassword({ props, data, refs })}>
                <span>{i18n.global.t("button.changePassword")}</span>
            </Button>
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => (
            <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/setting/user-mgt/users?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        )
    });
    return options;
};

export const openModalChangePassword = ({ data }) => {
    const refs = reactive({ changePassword: null });
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            title: i18n.global.t("views.changePassword.title"),
            width: "520px",
            okText: i18n.global.t("button.changePassword"),
            onOk: () => {
                refs.changePassword.onSubmit().then(() => {
                    notification["success"]({ message: i18n.global.t("views.changePassword.success") });
                    options.bind.onCancel();
                });
            }
        }),
        render: () => (
            <ChangePassword
                ref={e => {
                    if (e) refs.changePassword = e;
                }}
                id={data.id}
            ></ChangePassword>
        )
    });
    return options;
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "*",
                "refDepartment.*", "refDepartmentRegister.*", "refJobPosition.nameTh",
                "refParent.username", "refParent.email", "refParent.fullNameTh",
            ],
            sort: [{ name: "updateDate", type: "DESC" }]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
